import { formatDate } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { Branding, BrandingPost } from 'src/app/models/branding';
import { Organisation } from 'src/app/models/organisation';
import { User } from 'src/app/models/user';
import { IdentityBrandingService } from 'src/app/services/identityBranding.service';
import { LoggingService } from 'src/app/services/logging.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  templateUrl: './branding-tab-images.component.html'
})
export class BrandingTabImagesComponent implements OnInit {

  private _user!: User;
  get user(): User {
    return this._user;
  }
  @Input('user') set user(val: User){
    this._user = val;
  }
  translations:any = <any>{};
  branding: Branding = new Branding;
  platformLogo: File|null = null;
  platformLogoName: string|null = null;
  identityLogo: File|null = null;
  identityLogoName: string|null = null;
  favicon: File|null = null;
  faviconName: string|null = null;
  watermark: File|null = null;
  watermarkName: string|null = null;
  resetIdentityLogo: boolean = false;
  resetPlatformLogo: boolean = false;
  resetFavicon: boolean = false;
  resetWatermark: boolean = false;
  brandingPost: FormData = new FormData(); 
  brandingChanged: boolean = false;

  constructor(protected brandingService: IdentityBrandingService, protected translateService: TranslateService, protected toastService: ToastService,
    protected loggingService: LoggingService, protected sanitizer: DomSanitizer) {
    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
  }

  async ngOnInit() {
    // Must not be empty. It's approved by a comment.
  }

  getTranslations() {
    this.translateService.get([
      'Dialog.ElectronicallySignedBy',
      'Toasts.ErrorGettingBranding',
      'Toasts.ErrorDeletingBranding',
      'Toasts.ErrorSavingBranding',
      'Toasts.Error',
      'Toasts.Saved',
      'Toasts.ChangesSaved'
    ]).subscribe(translation => {
      this.translations.ElectronicallySignedBy = translation['Dialog.ElectronicallySignedBy'];
      this.translations.toastsErrorGettingBranding = translation['Toasts.ErrorGettingBranding'];
      this.translations.toastsErrorDeletingBranding = translation['Toasts.ErrorDeletingBranding'];
      this.translations.toastsErrorSavingBranding = translation['Toasts.ErrorSavingBranding'];
      this.translations.error = translation['Toasts.Error'];
      this.translations.toastSaved = translation['Toasts.Saved'];
      this.translations.toastChangedSaved = translation['Toasts.ChangesSaved'];
    });
  }

  public async uploadImage(imageType: string, event: any){
    switch(imageType){
      case "platformLogo": {
        this.platformLogo = event.target.files[0];
        this.platformLogoName = event.target.files[0].name;
        this.resetPlatformLogo = false;
        
        this.branding.platformLogoUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(event.target.files[0]));
        break;
      }
      case "identityLogo": {
        this.identityLogo = event.target.files[0];
        this.identityLogoName = event.target.files[0].name;
        this.resetIdentityLogo = false;

        this.branding.identityLogoUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(event.target.files[0]));
        break;
      }
      case "favicon": {
        this.favicon = event.target.files[0];
        this.faviconName = event.target.files[0].name;
        this.resetFavicon = false;

        this.branding.faviconUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(event.target.files[0]));
        break;
      }
      case "watermark": {
        this.watermark = event.target.files[0];
        this.watermarkName = event.target.files[0].name;
        this.resetWatermark = false;
        const canvas = <HTMLCanvasElement>document.getElementById('stampImage');
        const context = canvas!.getContext('2d');
        context?.clearRect(0,0, canvas.width, canvas.height);
        this.branding.stampUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(event.target.files[0]));
        break;
      }
    }
    this.brandingChanged = true;
    this.saveBranding();
  }

  public async removeImage(imageType: string){
    switch(imageType){
      case "platformLogo": {
        this.platformLogoName = null;
        this.branding.platformLogoUrl = null;
        this.resetPlatformLogo = true;
        break;
      }
      case "identityLogo": {
        this.identityLogoName = null;
        this.branding.identityLogoUrl = null;
        this.resetIdentityLogo = true;
        break;
      }
      case "favicon": {
        this.faviconName = null;
        this.branding.faviconUrl = null;
        this.resetFavicon = true;
        break;
      }
      case "watermark": {
        this.watermarkName = null;
        this.branding.stampUrl = null;
        this.resetWatermark = true;
        const canvas = <HTMLCanvasElement>document.getElementById('stampImage');
        const context = canvas!.getContext('2d');
        context?.clearRect(0,0, canvas.width, canvas.height);
        break;
      }
    }
    this.brandingChanged = true;
    this.saveBranding();
  }

  fillBrandingTabImages(data: any) {
    this.branding = data;
    this.platformLogoName = this.branding.platformLogoUrl !== undefined && this.branding.platformLogoUrl !== null ? this.branding.platformLogoUrl.toString().split('/').pop()! : null;
    this.identityLogoName = this.branding.identityLogoUrl !== undefined && this.branding.identityLogoUrl !== null ? this.branding.identityLogoUrl.toString().split('/').pop()! : null;
    this.faviconName = this.branding.faviconUrl !== undefined && this.branding.faviconUrl !== null ? this.branding.faviconUrl.toString().split('/').pop()! : null;
    this.watermarkName = this.branding.stampUrl !== undefined && this.branding.stampUrl !== null ? this.branding.stampUrl.toString().split('/').pop()! : null;
    const canvas = <HTMLCanvasElement>document.getElementById('stampImage');
    const context = canvas!.getContext('2d');
    if(this.branding.stampUrl === undefined || this.branding.stampUrl === null) return;
    let base_image = new Image();
    base_image.src = this.branding.stampUrl!.toString();
    let y = this;
    base_image.onload = function(){
      let date:string = formatDate(new Date(), "dd-MM-y", "en-US");
      let time:string = formatDate(new Date(), "HH:mm ZZZZZ", "en-US");
      context!.drawImage(base_image, 60, 17);
      if(base_image.src !== null) {
        context!.font = 'bold 14px Helvetica';
        context!.fillText("" + y.translations.ElectronicallySignedBy + "", 50, 40);
  
        context!.font = 'lighter 14px Helvetica';
        context!.fillText(y.user.fullName, 50, 60);  
        context!.fillText(date, 50, 85);
        context!.fillText(time + ' UTC', 50, 100);
      }
      context!.save();
    }
  }

  resetBranding(){
    this.resetPlatformLogo = false;
    this.resetIdentityLogo = false;
    this.resetFavicon = false;
    this.resetWatermark = false;
    this.brandingChanged = false;
    this.platformLogo = null;
    this.identityLogo = null;
    this.favicon = null;
    this.watermark = null;
  }

  saveImagesForBranding() {
    let uploadData: FormData = new FormData();
    if(this.identityLogo !== null) uploadData.append('IdentityLogo', this.identityLogo, this.identityLogoName!);
    if(this.platformLogo !== null) uploadData.append('PlatformLogo', this.platformLogo, this.platformLogoName!);
    if(this.favicon !== null) uploadData.append('Favicon', this.favicon, this.faviconName!);
    if(this.watermark !== null) uploadData.append('Stamp', this.watermark, this.watermarkName!);
    uploadData.append('ResetIdentityLogo', this.resetIdentityLogo.toString());
    uploadData.append('ResetPlatformLogo', this.resetPlatformLogo.toString());
    uploadData.append('ResetFavicon', this.resetFavicon.toString());
    uploadData.append('ResetStamp', this.resetWatermark.toString());
    return uploadData;
  }

  protected getBranding() {}
  public saveBranding() {}

}

@Component({
  selector: 'app-branding-tab-images-organisation',
  templateUrl: './branding-tab-images.component.html'
})
export class BrandingTabImagesOrganisationComponent extends BrandingTabImagesComponent {

  private _organisation!: any;
  get organisation(): any {
    return this._organisation;
  }
  @Input('organisation') set organisation(val: any){
    this._organisation = val;
    if(this._organisation){
      this.getBranding();
    }
  }

  constructor(protected override brandingService: IdentityBrandingService, protected override translateService: TranslateService, protected override toastService: ToastService,
    protected override loggingService: LoggingService, protected override sanitizer: DomSanitizer) {
      super(brandingService, translateService, toastService, loggingService, sanitizer);
  }

  protected override getBranding() {
    this.brandingService.getBrandingOrganisation(this.organisation.guid)
    .then((response: any) => {
      lastValueFrom(response)
      .then((data: any) => {
        this.fillBrandingTabImages(data);
      });
    })
    .catch(error => {
      this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorGettingBranding + "");
      this.loggingService.logException(error)
    });
  }

  public override saveBranding() {
    const uploadData = this.saveImagesForBranding();
    this.brandingService.postBrandingOrganisation(this.organisation.guid, uploadData)
    .then(data => {
      lastValueFrom(data).then(() => {
        this.resetBranding();
        this.toastService.showSuccess(this.translations.toastSaved, this.translations.toastChangedSaved);
        this.getBranding();
      })
      .catch(() => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorSavingBranding + "");
      });
    })
    .catch(error => this.loggingService.logException(error));
  }
}

@Component({
  selector: 'app-branding-tab-images-whitelabel',
  templateUrl: './branding-tab-images.component.html'
})
export class BrandingTabImagesWhiteLabelComponent extends BrandingTabImagesComponent {

  private _whiteLabel!: any;
  get whiteLabel(): any {
    return this._whiteLabel;
  }
  @Input('whiteLabel') set whiteLabel(val: any){
    this._whiteLabel = val;
    if(this._whiteLabel){
      this.getBranding();
    }
  }

  constructor(protected override brandingService: IdentityBrandingService, protected override translateService: TranslateService, protected override toastService: ToastService,
    protected override loggingService: LoggingService, protected override sanitizer: DomSanitizer) {
      super(brandingService, translateService, toastService, loggingService, sanitizer);
  }

  protected override getBranding() {
    this.brandingService.getBrandingWhiteLabel(this.whiteLabel.guid)
    .then((response: any) => {
      lastValueFrom(response)
      .then((data: any) => {
        this.fillBrandingTabImages(data);
      });
    })
    .catch(error => {
      this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorGettingBranding + "");
      this.loggingService.logException(error)
    });
  }

  public override saveBranding() {
    const uploadData = this.saveImagesForBranding();
    this.brandingService.postBrandingWhiteLabel(this.whiteLabel.guid, uploadData)
    .then(data => {
      lastValueFrom(data).then(() => {
        this.resetBranding();
        this.toastService.showSuccess(this.translations.toastSaved, this.translations.toastChangedSaved);
        this.getBranding();
      })
      .catch(() => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorSavingBranding + "");
      });
    })
    .catch(error => this.loggingService.logException(error));
  }

}

@Component({
  selector: 'app-branding-tab-images-system',
  templateUrl: './branding-tab-images.component.html'
})
export class BrandingTabImagesSystemComponent extends BrandingTabImagesComponent {

  private _system!: any;
  get system(): any {
    return this._system;
  }
  @Input('system') set system(val: any){
    this._system = val;
    if(this._system){
      this.getBranding();
    }
  }

  constructor(protected override brandingService: IdentityBrandingService, protected override translateService: TranslateService, protected override toastService: ToastService,
    protected override loggingService: LoggingService, protected override sanitizer: DomSanitizer) {
      super(brandingService, translateService, toastService, loggingService, sanitizer);
  }

  protected override getBranding() {
    this.brandingService.getBrandingSystem(this.system.guid)
    .then((response: any) => {
      lastValueFrom(response)
      .then((data: any) => {
        this.fillBrandingTabImages(data);
      });
    })
    .catch(error => {
      this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorGettingBranding + "");
      this.loggingService.logException(error)
    });
  }

  public override saveBranding() {
    const uploadData = this.saveImagesForBranding();
    this.brandingService.postBrandingSystem(this.system.guid, uploadData)
    .then(data => {
      lastValueFrom(data).then(() => {
        this.resetBranding();
        this.toastService.showSuccess(this.translations.toastSaved, this.translations.toastChangedSaved);
        this.getBranding();
      })
      .catch(() => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorSavingBranding + "");
      });
    })
    .catch(error => this.loggingService.logException(error));
  }

}